import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PanelRightClose, PanelRightOpen, Plus } from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Toast from "../../components/ui/Toast";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth";
import useMediaQuery from "../../hooks/useMediaQuery";
import useTaskBoardSocket from "../../hooks/useTaskBoardSocket";
import {
  boardService,
  boardTaskService,
  taskDiscussionService,
  apiErrorMessage,
} from "../../api/taskBoardService";
import BoardColumn from "../../components/task-board/BoardColumn";
import BoardFilters from "../../components/task-board/BoardFilters";
import SprintPanel from "../../components/task-board/SprintPanel";
import TaskFormModal from "../../components/task-board/TaskFormModal";
import { EXPORTABLE_STATUSES, exportColumnToExcel } from "../../components/task-board/exportColumn";
import PlanTaskModal from "../../components/task-board/PlanTaskModal";
import CreateSprintModal from "../../components/task-board/CreateSprintModal";
import { syncSprintPlanDocument, syncSprintTimelineDocument } from "../../components/task-board/sprintDocuments";
import { ErrorState, daysUntil, formatDate } from "../../components/task-board/shared";
import {
  BACKLOG_STATUS,
  FILTER_KEYS,
  NONE,
  PLANNING_LABELS,
  STATUS_LABELS,
} from "../../components/task-board/constants";
import {
  boardReducer,
  createEventDeduper,
  currentSprint,
  editableTaskFields,
  filterVisibility,
  filtersFromParams,
  missingPlanningFields,
  needsPlanning,
  statusesFor,
  groupByStatus,
  initialBoardState,
} from "../../components/task-board/boardState";

const PANEL_PREF_KEY = "taskBoard:sprintPanelOpen";
const readPanelPref = () => {
  try {
    // Closed unless the user left it open last time.
    return localStorage.getItem(PANEL_PREF_KEY) === "true";
  } catch {
    return false;
  }
};

const byId = (list = []) => Object.fromEntries(list.map((x) => [x._id, x]));

const ConnectionBadge = ({ status }) => {
  const map = {
    live: ["pill-ok", "Live"],
    connecting: ["pill-warn animate-pulse", "Connecting…"],
    offline: ["pill-danger animate-pulse", "Offline — reconnecting"],
  };
  const [tone, label] = map[status] || map.connecting;
  return (
    <span
      className={`pill ${tone} align-middle !h-[22px] !text-[11.5px] tracking-normal font-sans`}
      title="Realtime board updates"
      role="status"
    >
      {label}
    </span>
  );
};

/**
 * Unified task board. Server filtering + visibility, local state keyed by task
 * id, optimistic drag & drop, realtime sync over the existing socket server.
 * Filters (and therefore the selected sprint) live in the URL.
 */
const TaskBoard = () => {
  const { auth } = useAuth();
  const token = auth?.accessToken || localStorage.getItem("accessToken");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const filtersKey = JSON.stringify(filters);
  // Client-side view options (deadline filter + sort) live in the URL too.
  const deadlineParam = searchParams.get("deadline") || "";
  const sortParam = searchParams.get("sort") || "";
  const dueParam = searchParams.get("due") || "";
  const view = useMemo(
    () => ({ deadline: deadlineParam, sort: sortParam, due: dueParam }),
    [deadlineParam, sortParam, dueParam],
  );
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [options, setOptions] = useState(null);
  const isAdminRef = useRef(false);
  isAdminRef.current = Boolean(options?.access?.isAdmin);
  // Filters used to decide whether a live task belongs on the board; admins
  // keep Product Backlog tasks under any sprint filter (mirrors the API).
  const matchFilters = () => ({ ...filtersRef.current, backlogAllSprints: isAdminRef.current });
  // Tasks with comments from others since this user last opened them.
  const [unreadIds, setUnreadIds] = useState(() => new Set());
  const [optionsError, setOptionsError] = useState("");
  const [state, dispatch] = useReducer(boardReducer, initialBoardState);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [modal, setModal] = useState(null); // { task } | { defaults }
  // Planning a task out of Product Backlog: { task, toStatus }.
  const [planning, setPlanning] = useState(null);
  // Product Backlog tasks ticked for "Create Sprint" (admins).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [creatingSprint, setCreatingSprint] = useState(false);
  const [panelOpen, setPanelOpen] = useState(readPanelPref);
  const [sprintRefreshKey, setSprintRefreshKey] = useState(0);
  const { toast, showSuccess, showError, hideToast } = useToast();
  const isDuplicateEvent = useRef(createEventDeduper()).current;
  // Task activity (comments / history / files) fan-out to the open task modal.
  const activityListeners = useRef(new Set());
  const subscribeActivity = useCallback((fn) => {
    activityListeners.current.add(fn);
    return () => activityListeners.current.delete(fn);
  }, []);
  const emitActivity = (event) => activityListeners.current.forEach((fn) => fn(event));
  const requestSeq = useRef(0);
  const sprintBumpTimer = useRef(null);

  // ---------------- data loading ----------------

  const loadOptions = useCallback(async () => {
    try {
      const res = await boardService.getOptions();
      setOptions(res.data);
      setOptionsError("");
    } catch (err) {
      setOptionsError(apiErrorMessage(err, "Could not load board options"));
    }
  }, []);

  const loadTasks = useCallback(async ({ silent = false } = {}) => {
    const seq = ++requestSeq.current;
    if (!silent) setLoading(true);
    try {
      const res = await boardTaskService.list(filtersRef.current);
      if (seq !== requestSeq.current) return; // a newer request (filters changed) wins
      dispatch({ type: "loaded", tasks: res.data });
      setUnreadIds(new Set(res.data.filter((t) => t.unreadComments).map((t) => t._id)));
      setTruncated(Boolean(res.meta?.truncated));
      setLoadError("");
    } catch (err) {
      if (seq === requestSeq.current) setLoadError(apiErrorMessage(err, "Could not load tasks"));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Squad / assignee filters only exist for users who can use them; drop a
  // hidden one that arrives in the URL (e.g. an old link) so it cannot
  // silently narrow the board.
  const visibleFilters = useMemo(() => filterVisibility(options?.access), [options]);
  useEffect(() => {
    if (!options) return;
    const hidden = ["squad", "assignee"].filter((k) => !visibleFilters[k] && searchParams.has(k));
    if (!hidden.length) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        hidden.forEach((k) => next.delete(k));
        return next;
      },
      { replace: true },
    );
  }, [options, visibleFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // First visit without a sprint in the URL: default the sprint filter to the
  // sprint running today (by its dates). Tasks load only after this decision so
  // the board is not fetched twice. A sprint chosen or cleared later is kept.
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (defaultsApplied) return;
    if (optionsError) return setDefaultsApplied(true);
    if (!options) return;
    // All defaults go into ONE URL update (separate setSearchParams calls in
    // the same tick overwrite each other).
    const defaults = {};
    if (!searchParams.has("sprint")) {
      const sprint = currentSprint(options.sprints);
      if (sprint) defaults.sprint = sprint._id;
    }
    // Non-admins start on their own squad: a squad lead sees all of its
    // tasks, a member sees their own. Both filters can be set back to "All".
    const { access } = options;
    if (!access.isAdmin && !searchParams.has("squad") && !searchParams.has("assignee")) {
      const home =
        access.memberships.find((m) => m.role === "SQUAD_LEAD") || access.memberships[0];
      if (home) {
        defaults.squad = home.squadId;
        if (home.role !== "SQUAD_LEAD") defaults.assignee = access.userId;
      }
    }
    if (Object.keys(defaults).length) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(defaults).forEach(([k, v]) => next.set(k, v));
          return next;
        },
        { replace: true },
      );
    }
    setDefaultsApplied(true);
  }, [options, optionsError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultsApplied) loadTasks();
  }, [filtersKey, loadTasks, defaultsApplied]);

  const bumpSprintPanel = useCallback(() => {
    if (!filtersRef.current.sprint) return;
    clearTimeout(sprintBumpTimer.current);
    sprintBumpTimer.current = setTimeout(() => setSprintRefreshKey((k) => k + 1), 400);
  }, []);
  useEffect(() => () => clearTimeout(sprintBumpTimer.current), []);

  // ---------------- realtime ----------------

  const connection = useTaskBoardSocket({
    token,
    onTask: (task, event) => {
      if (isDuplicateEvent(event?.eventId)) return;
      dispatch({ type: "upsert", task, filters: matchFilters() });
      bumpSprintPanel();
    },
    onTaskRemoved: (event) => {
      if (isDuplicateEvent(event?.eventId)) return;
      dispatch({ type: "remove", taskId: event.taskId, version: event.version });
      bumpSprintPanel();
    },
    onTaskActivity: (event) => {
      if (isDuplicateEvent(event?.eventId)) return;
      emitActivity(event);
      // Someone else commented on a task that is not open: show the bell.
      const commentedByOther = (event.activities || []).some(
        (a) => a.type === "comment_added" && a.actor?._id !== optionsRef.current?.access?.userId,
      );
      if (commentedByOther && openTaskIdRef.current !== event.taskId) {
        setUnreadIds((prev) => (prev.has(event.taskId) ? prev : new Set(prev).add(event.taskId)));
      }
    },
    onCatalogChanged: (event) => {
      loadOptions();
      if (event.entity === "sprint" && event.id === filtersRef.current.sprint) bumpSprintPanel();
    },
    // Reconnected or memberships changed: recover anything we missed.
    onResync: () => {
      emitActivity({ type: "resync" });
      loadOptions();
      loadTasks({ silent: true });
      bumpSprintPanel();
    },
  });

  // ---------------- filters ----------------

  const setFilter = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        [...FILTER_KEYS, "deadline", "sort", "due"].forEach((k) => next.delete(k));
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ---------------- mutations ----------------

  const applyServerTask = useCallback((task) => {
    dispatch({ type: "upsert", task, filters: matchFilters() });
    bumpSprintPanel();
  }, [bumpSprintPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPending = (taskId, on) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(taskId);
      else next.delete(taskId);
      return next;
    });

  const tasksRef = useRef(state.tasks);
  tasksRef.current = state.tasks;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleDropTask = useCallback(
    async (taskId, toStatus, position) => {
      const snapshot = tasksRef.current[taskId];
      if (!snapshot) return;
      const sameColumn = snapshot.status === toStatus;
      // Dropped back where it was: nothing to do.
      if (sameColumn && (position === undefined || position === snapshot.position)) return;

      // Leaving Product Backlog (or completing older, incomplete data) goes
      // through the planning modal; the task only moves once it is saved.
      if (!sameColumn && needsPlanning(snapshot, toStatus)) {
        const missing = missingPlanningFields(snapshot);
        const editable = editableTaskFields(optionsRef.current?.access, snapshot);
        const blocked = missing.filter((f) => !editable.has(f));
        if (snapshot.status !== BACKLOG_STATUS && blocked.length) {
          showError(
            `This task needs ${blocked.map((f) => PLANNING_LABELS[f].toLowerCase()).join(", ")} before it can move. Ask an admin to complete it.`,
          );
          return;
        }
        setPlanning({ task: snapshot, toStatus, position });
        return;
      }

      // Optimistic: move/reorder now, reconcile with the server's copy (or roll back).
      const changes = position === undefined ? { status: toStatus } : { status: toStatus, position };
      dispatch({ type: "patchLocal", taskId, changes });
      setPending(taskId, true);
      try {
        const res = await boardTaskService.move(taskId, toStatus, position);
        applyServerTask(res.data);
      } catch (err) {
        dispatch({ type: "rollback", snapshot });
        if (err?.response?.status === 404) dispatch({ type: "remove", taskId });
        // Someone cleared required data meanwhile: ask for it instead of failing.
        if (err?.response?.data?.code === "PLANNING_REQUIRED") setPlanning({ task: snapshot, toStatus, position });
        else showError(apiErrorMessage(err, "Could not move the task"));
      } finally {
        setPending(taskId, false);
      }
    },
    [applyServerTask, showError],
  );

  const canDrag = useCallback((task) => !pendingIds.has(task._id), [pendingIds]);
  const openTask = useCallback((task) => setModal({ task }), []);

  // Opening a task clears its bell; closing marks it seen again so comments
  // that arrived while it was open don't light it up afterwards.
  const openTaskId = modal?.task?._id || null;
  const openTaskIdRef = useRef(null);
  openTaskIdRef.current = openTaskId;
  useEffect(() => {
    if (!openTaskId) return undefined;
    const markSeen = () => {
      setUnreadIds((prev) => {
        if (!prev.has(openTaskId)) return prev;
        const next = new Set(prev);
        next.delete(openTaskId);
        return next;
      });
      taskDiscussionService.markSeen(openTaskId).catch(() => {});
    };
    markSeen();
    return markSeen;
  }, [openTaskId]);
  // Create a task in a column ("+") or from the header button. Product
  // Backlog (admins) takes only the basic fields; any other column also asks
  // for squad/sprint/assignee/due date/priority. Relations are pre-filled
  // from the active filters where they make sense.
  const openCreate = useCallback((status, draft) => {
    const access = optionsRef.current?.access;
    const target = status || (access?.isAdmin ? BACKLOG_STATUS : "sprint_backlog");
    const f = filtersRef.current;
    const defaults = { status: target };
    if (f.project && f.project !== NONE) defaults.projects = [f.project];
    if (target !== BACKLOG_STATUS) {
      if (f.sprint && f.sprint !== NONE) defaults.sprint = f.sprint;
      const leads = (access?.memberships || []).filter((m) => m.role === "SQUAD_LEAD");
      if (f.squad && (access?.isAdmin || leads.some((m) => m.squadId === f.squad))) defaults.squad = f.squad;
      else if (!access?.isAdmin && leads.length === 1) defaults.squad = leads[0].squadId;
    }
    // `draft` ({ title, description }) pre-fills the form, e.g. from an employee request.
    setModal({ defaults: draft ? { ...defaults, ...draft } : defaults });
  }, []);

  // Deep link (e.g. from a mention notification): /task-board?task=<id>
  // opens that task's details once, then drops the parameter.
  useEffect(() => {
    const taskId = searchParams.get("task");
    if (!options || !taskId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("task");
        return next;
      },
      { replace: true },
    );
    boardTaskService
      .get(taskId)
      .then((res) => setModal({ task: res.data }))
      .catch(() => showError("This task does not exist or you no longer have access to it"));
  }, [options, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Create task" quick action from other pages (e.g. Dashboard).
  useEffect(() => {
    if (!options || !location.state?.openCreateTask) return;
    if (canCreate) openCreate(undefined, location.state.taskDraft);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [options, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePanel = () => {
    setPanelOpen((open) => {
      try {
        localStorage.setItem(PANEL_PREF_KEY, String(!open));
      } catch {
        /* storage unavailable: preference just is not remembered */
      }
      return !open;
    });
  };

  // ---------------- derived ----------------

  const lookups = useMemo(
    () => ({
      projects: byId(options?.projects),
      squads: byId(options?.squads),
      sprints: byId(options?.sprints),
    }),
    [options],
  );
  const columns = useMemo(() => groupByStatus(state.tasks, view), [state.tasks, view]);
  // Refs keep the export handler stable so memoized columns don't re-render.
  const exportRef = useRef({});
  exportRef.current = { columns, lookups };
  const handleExport = useCallback(
    (status) => {
      const { columns: cols, lookups: lk } = exportRef.current;
      const sprintId = filtersRef.current.sprint;
      exportColumnToExcel({
        status,
        tasks: cols[status] || [],
        lookups: lk,
        // Backlog tasks have no sprint, so only name the sprint for other columns.
        sprintName: status !== BACKLOG_STATUS && sprintId ? lk.sprints[sprintId]?.name : null,
      });
    },
    [],
  );
  // Product Backlog is an admin-only column.
  const boardStatuses = statusesFor(options?.access);
  const visibleStatuses = filters.status
    ? boardStatuses.filter((s) => s.value === filters.status)
    : boardStatuses;
  const taskCount = Object.keys(state.tasks).length;
  const hasFilters = Object.keys(filters).length > 0 || Boolean(view.deadline || view.sort || view.due);
  // Admins and squad leads create tasks (members don't).
  const canCreate = Boolean(
    options?.access.isAdmin || options?.access.memberships.some((m) => m.role === "SQUAD_LEAD"),
  );
  // Only tasks still in Product Backlog stay selected (others moved meanwhile).
  const backlogTasks = useMemo(() => columns[BACKLOG_STATUS] || [], [columns]);
  const selectedTasks = useMemo(
    () => backlogTasks.filter((t) => selectedIds.has(t._id)),
    [backlogTasks, selectedIds],
  );
  const backlogSelection = useMemo(
    () =>
      options?.access.isAdmin
        ? {
            selectedIds: new Set(selectedTasks.map((t) => t._id)),
            selectedCount: selectedTasks.length,
            onToggle: (id) =>
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              }),
            onToggleAll: () =>
              setSelectedIds(selectedTasks.length === backlogTasks.length ? new Set() : new Set(backlogTasks.map((t) => t._id))),
            onClear: () => setSelectedIds(new Set()),
            onCreateSprint: () => setCreatingSprint(true),
          }
        : undefined,
    [options, selectedTasks, backlogTasks],
  );
  const selectedSprintId = filters.sprint && filters.sprint !== NONE ? filters.sprint : "";
  const selectedSprint = lookups.sprints[selectedSprintId];

  const panel = (
    <SprintPanel
      sprintId={selectedSprintId}
      sprints={options?.sprints || []}
      refreshKey={sprintRefreshKey}
      onSelectSprint={(id) => setFilter("sprint", id)}
      // Product Backlog (drafts) is never part of a sprint summary.
      statuses={boardStatuses.filter((s) => s.value !== BACKLOG_STATUS)}
      squad={filters.squad || ""}
      assignee={filters.assignee || ""}
      canEditMilestones={Boolean(options?.access.isAdmin)}
      onClose={togglePanel}
    />
  );
  const showPanel = panelOpen;
  const sprintLeft = selectedSprint?.endDate ? daysUntil(selectedSprint.endDate) : null;
  const sprintSubtitle = selectedSprint
    ? [
        selectedSprint.name,
        selectedSprint.startDate &&
          `${formatDate(selectedSprint.startDate, { day: "numeric", month: "short" })} → ${formatDate(selectedSprint.endDate, { day: "numeric", month: "short" })}`,
        selectedSprint.status === "active" && sprintLeft !== null && sprintLeft >= 0
          ? sprintLeft === 0
            ? "ends today"
            : `${sprintLeft} day${sprintLeft === 1 ? "" : "s"} left`
          : selectedSprint.status,
      ]
        .filter(Boolean)
        .join(" · ")
    : "All work across projects, squads and sprints";

  return (
    <div
      className="transition-[padding] duration-[350ms]"
      style={{
        paddingRight: showPanel && isDesktop ? 356 : 0,
        transitionTimingFunction: "cubic-bezier(.2,.9,.3,1)",
      }}
    >
    <PageWrapper
      title={
        <span className="inline-flex items-center gap-3">
          Task Board <ConnectionBadge status={connection} />
        </span>
      }
      subtitle={sprintSubtitle}
      actions={
        <>
          <button type="button" className="btn-ghost" onClick={togglePanel} aria-pressed={showPanel}>
            {showPanel ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            <span className="hidden sm:inline">Sprint panel</span>
          </button>
          {canCreate && (
            <button type="button" className="btn-primary" onClick={() => openCreate()}>
              <Plus className="w-4 h-4" /> New task
            </button>
          )}
        </>
      }
    >
      {optionsError && <div className="mb-4"><ErrorState message={optionsError} onRetry={loadOptions} /></div>}

      {options && (
        <BoardFilters
          filters={filters}
          options={options}
          visible={visibleFilters}
          view={view}
          onChange={setFilter}
          onViewChange={setFilter}
          onClear={clearFilters}
          matchCount={Object.values(columns).reduce((n, list) => n + list.length, 0)}
        />
      )}

      {(
        <div className="mt-4 flex gap-4 min-h-[420px]" style={{ height: "calc(100vh - 252px)" }}>
          <div className="flex-1 min-w-0 flex flex-col gap-3">
            {loadError && <ErrorState message={loadError} onRetry={() => loadTasks()} />}
            {truncated && (
              <p className="text-xs px-3 py-2 rounded-xl bg-warn-soft text-warn">
                Showing the most recently updated tasks only. Narrow the filters to see everything.
              </p>
            )}
            {state.loaded && !loading && taskCount === 0 && !loadError && (
              <div className="flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-xl bg-raise text-muted">
                {hasFilters ? "No tasks match these filters." : "No tasks yet."}
                {hasFilters ? (
                  <button type="button" onClick={clearFilters} className="font-medium text-accent-ink hover:underline">
                    Clear filters
                  </button>
                ) : (
                  canCreate && (
                    <button type="button" onClick={() => openCreate()} className="font-medium text-accent-ink hover:underline">
                      Create the first task
                    </button>
                  )
                )}
              </div>
            )}

            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden pb-2">
              {loading && !state.loaded ? (
                <div className="flex gap-3 h-full" aria-busy="true">
                  {boardStatuses.map((s) => (
                    <div key={s.value} className="w-72 shrink-0 rounded-[18px] bg-panel2 border border-line animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className={`flex gap-3 h-full ${loading ? "opacity-70" : ""}`}>
                  {visibleStatuses.map((status) => (
                    <BoardColumn
                      key={status.value}
                      status={status}
                      tasks={columns[status.value]}
                      lookups={lookups}
                      pendingIds={pendingIds}
                      unreadIds={unreadIds}
                      canDrag={canDrag}
                      canCreate={canCreate}
                      onDropTask={handleDropTask}
                      onOpenTask={openTask}
                      onCreate={openCreate}
                      onExport={EXPORTABLE_STATUSES.includes(status.value) ? handleExport : undefined}
                      selection={status.value === BACKLOG_STATUS ? backlogSelection : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* Sprint panel: fixed, non-modal, rendered at the app root. The board
          is pushed left (padding above) so nothing hides underneath. */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {showPanel && (
            <motion.aside
              key="sprint-panel"
              aria-label="Sprint details"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
              className="fixed z-40 right-5 top-[84px] bottom-5 overflow-y-auto rounded-[22px] bg-panel-solid border border-line2 shadow-modal"
              style={{ width: "min(340px, calc(100vw - 40px))" }}
            >
              {panel}
            </motion.aside>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {modal && options && (
        <TaskFormModal
          isOpen
          // The snapshot taken when the form opened: its version is what the
          // edit is based on, so concurrent changes surface as a conflict.
          task={modal.task || null}
          defaults={modal.defaults}
          options={options}
          onClose={() => setModal(null)}
          onConflict={(current) => {
            applyServerTask(current);
            setModal({ task: current });
          }}
          onSaved={(task, message) => {
            applyServerTask(task);
            setModal(null);
            showSuccess(message);
          }}
          subscribeActivity={subscribeActivity}
          onDeleted={(taskId) => {
            dispatch({ type: "remove", taskId });
            bumpSprintPanel();
            setModal(null);
            showSuccess("Task deleted");
          }}
        />
      )}

      {creatingSprint && selectedTasks.length > 0 && (
        <CreateSprintModal
          tasks={selectedTasks}
          onClose={() => setCreatingSprint(false)}
          onCreated={({ sprint, tasks }) => {
            tasks.forEach(applyServerTask);
            setSelectedIds(new Set());
            setCreatingSprint(false);
            loadOptions();
            showSuccess(`Sprint “${sprint.name}” created with ${tasks.length} task${tasks.length === 1 ? "" : "s"}`);
            // The Sprint Plan PDF is generated and attached automatically. If
            // that fails the sprint is unaffected; its Sprint Plan tab retries.
            // Plan, then timeline (one after the other).
            syncSprintPlanDocument(sprint)
              .then(() => syncSprintTimelineDocument(sprint))
              .then(() => showSuccess(`Sprint plan and timeline PDFs saved to “${sprint.name}”`))
              .catch(() =>
                showError(`Sprint created, but its PDFs were not saved. Open the sprint's Sprint Plan and Timeline tabs and press Retry.`),
              );
          }}
        />
      )}

      {planning && options && (
        <PlanTaskModal
          // Latest local copy (version) of the task being planned.
          task={state.tasks[planning.task._id] || planning.task}
          toStatus={planning.toStatus}
          position={planning.position}
          options={options}
          onClose={() => setPlanning(null)}
          onConflict={applyServerTask}
          onSaved={(task) => {
            applyServerTask(task);
            setPlanning(null);
            showSuccess(`Task moved to ${STATUS_LABELS[task.status]}`);
          }}
        />
      )}

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </PageWrapper>
    </div>
  );
};

export default TaskBoard;
